import type { Project, Task, BaselineTask, Portfolio, Program } from '@/types';

// =============================================================================
// ProjexHQ demo seed data. TODAY (demo "now") = 2026-08-19.
// All project/phase/milestone end dates are on or after 2026-09-01; project
// end dates spread from Sep 2026 to mid-2027. Per-task percentComplete never
// shows progress on a task whose planned start is still in the future.
// Completed early tasks legitimately sit in the past for in-flight projects.
// =============================================================================

const erpTasks: Task[] = [
  { taskId: 1, wbs: '1.0', task: 'Planning Phase', type: 'summary', startDate: '2026-04-06', endDate: '2026-05-16', duration: 41, percentComplete: 100, predecessors: '', successors: '6', resources: [], allocation: [] },
  { taskId: 2, wbs: '1.1', task: 'Project Charter Development', type: 'task', startDate: '2026-04-06', endDate: '2026-04-12', duration: 7, percentComplete: 100, predecessors: '', successors: '3', resources: ['Sarah Johnson'], allocation: [100] },
  { taskId: 3, wbs: '1.2', task: 'Stakeholder Analysis', type: 'task', startDate: '2026-04-13', endDate: '2026-04-18', duration: 6, percentComplete: 100, predecessors: '2FS', successors: '4', resources: ['Sarah Johnson', 'Maria Garcia'], allocation: [75, 100] },
  { taskId: 4, wbs: '1.3', task: 'Requirements Gathering', type: 'task', startDate: '2026-04-20', endDate: '2026-05-05', duration: 16, percentComplete: 100, predecessors: '3FS', successors: '5', resources: ['Maria Garcia', 'David Kim'], allocation: [100, 50] },
  { taskId: 5, wbs: '1.4', task: 'Project Plan Finalization', type: 'task', startDate: '2026-05-06', endDate: '2026-05-16', duration: 11, percentComplete: 100, predecessors: '4FS', successors: '7', resources: ['Sarah Johnson'], allocation: [100] },
  { taskId: 6, wbs: '2.0', task: 'Design Phase', type: 'summary', startDate: '2026-05-17', endDate: '2026-07-19', duration: 64, percentComplete: 100, predecessors: '1FS', successors: '11', resources: [], allocation: [] },
  { taskId: 7, wbs: '2.1', task: 'Current State Assessment', type: 'task', startDate: '2026-05-17', endDate: '2026-05-30', duration: 14, percentComplete: 100, predecessors: '5FS', successors: '8', resources: ['Maria Garcia', 'Robert Taylor'], allocation: [100, 50] },
  { taskId: 8, wbs: '2.2', task: 'Solution Architecture Design', type: 'task', startDate: '2026-05-31', endDate: '2026-06-17', duration: 18, percentComplete: 100, predecessors: '7FS', successors: '9', resources: ['David Kim'], allocation: [100] },
  { taskId: 9, wbs: '2.3', task: 'Data Migration Strategy', type: 'task', startDate: '2026-06-18', endDate: '2026-07-05', duration: 18, percentComplete: 100, predecessors: '8FS', successors: '10', resources: ['David Kim', 'Robert Taylor'], allocation: [75, 100] },
  { taskId: 10, wbs: '2.4', task: 'Interface Design', type: 'task', startDate: '2026-07-06', endDate: '2026-07-14', duration: 9, percentComplete: 100, predecessors: '9FS', successors: '11', resources: ['Maria Garcia', 'James Chen'], allocation: [50, 75] },
  { taskId: 11, wbs: '2.5', task: 'Design Review & Approval', type: 'milestone', startDate: '2026-07-16', endDate: '2026-07-19', duration: 4, percentComplete: 100, predecessors: '10FS', successors: '12', resources: ['Sarah Johnson', 'David Kim'], allocation: [100, 100] },
  { taskId: 12, wbs: '3.0', task: 'Build Phase', type: 'summary', startDate: '2026-07-20', endDate: '2026-09-17', duration: 60, percentComplete: 50, predecessors: '6FS', successors: '21', resources: [], allocation: [] },
  { taskId: 13, wbs: '3.1', task: 'Environment Setup', type: 'task', startDate: '2026-07-20', endDate: '2026-07-25', duration: 6, percentComplete: 100, predecessors: '11FS', successors: '14,15,16', resources: ['David Kim', 'James Chen'], allocation: [75, 100] },
  { taskId: 14, wbs: '3.2', task: 'Finance Module Configuration', type: 'task', startDate: '2026-07-27', endDate: '2026-08-16', duration: 21, percentComplete: 100, predecessors: '13FS', successors: '17', resources: ['James Chen', 'Robert Taylor'], allocation: [100, 75] },
  { taskId: 15, wbs: '3.3', task: 'HR Module Configuration', type: 'task', startDate: '2026-07-27', endDate: '2026-08-14', duration: 19, percentComplete: 100, predecessors: '13SS', successors: '18', resources: ['James Chen'], allocation: [100] },
  { taskId: 16, wbs: '3.4', task: 'Supply Chain Module Configuration', type: 'task', startDate: '2026-07-30', endDate: '2026-08-23', duration: 25, percentComplete: 85, predecessors: '13SS+2d', successors: '19', resources: ['David Kim', 'James Chen'], allocation: [100, 75] },
  { taskId: 17, wbs: '3.5', task: 'Data Migration - Finance', type: 'task', startDate: '2026-08-18', endDate: '2026-08-30', duration: 13, percentComplete: 10, predecessors: '14FS', successors: '20', resources: ['Robert Taylor', 'David Kim'], allocation: [100, 50] },
  { taskId: 18, wbs: '3.6', task: 'Data Migration - HR', type: 'task', startDate: '2026-08-15', endDate: '2026-08-26', duration: 12, percentComplete: 35, predecessors: '15FS', successors: '20', resources: ['Robert Taylor'], allocation: [100] },
  { taskId: 19, wbs: '3.7', task: 'Data Migration - Supply Chain', type: 'task', startDate: '2026-08-25', endDate: '2026-09-06', duration: 13, percentComplete: 0, predecessors: '16FS', successors: '20', resources: ['Robert Taylor', 'James Chen'], allocation: [100, 50] },
  { taskId: 20, wbs: '3.8', task: 'Integration Development', type: 'task', startDate: '2026-09-07', endDate: '2026-09-17', duration: 11, percentComplete: 0, predecessors: '17FS,18FS,19FS', successors: '22', resources: ['James Chen', 'David Kim'], allocation: [100, 100] },
  { taskId: 21, wbs: '4.0', task: 'Testing Phase', type: 'summary', startDate: '2026-09-19', endDate: '2026-10-15', duration: 27, percentComplete: 0, predecessors: '12FS', successors: '26', resources: [], allocation: [] },
  { taskId: 22, wbs: '4.1', task: 'Unit Testing', type: 'task', startDate: '2026-09-19', endDate: '2026-09-24', duration: 6, percentComplete: 0, predecessors: '20FS', successors: '23', resources: ['James Chen', 'David Kim'], allocation: [50, 50] },
  { taskId: 23, wbs: '4.2', task: 'System Integration Testing', type: 'task', startDate: '2026-09-26', endDate: '2026-10-04', duration: 9, percentComplete: 0, predecessors: '22FS', successors: '24', resources: ['Maria Garcia', 'Robert Taylor'], allocation: [100, 100] },
  { taskId: 24, wbs: '4.3', task: 'User Acceptance Testing', type: 'task', startDate: '2026-10-05', endDate: '2026-10-12', duration: 8, percentComplete: 0, predecessors: '23FS', successors: '25', resources: ['Maria Garcia'], allocation: [100] },
  { taskId: 25, wbs: '4.4', task: 'Test Sign-off', type: 'milestone', startDate: '2026-10-13', endDate: '2026-10-15', duration: 3, percentComplete: 0, predecessors: '24FS', successors: '27', resources: ['Sarah Johnson', 'Maria Garcia'], allocation: [100, 100] },
  { taskId: 26, wbs: '5.0', task: 'Deployment Phase', type: 'summary', startDate: '2026-10-16', endDate: '2026-10-30', duration: 15, percentComplete: 0, predecessors: '21FS', successors: '', resources: [], allocation: [] },
  { taskId: 27, wbs: '5.1', task: 'Production Deployment', type: 'task', startDate: '2026-10-16', endDate: '2026-10-22', duration: 7, percentComplete: 0, predecessors: '25FS', successors: '28', resources: ['David Kim', 'James Chen'], allocation: [100, 100] },
  { taskId: 28, wbs: '5.2', task: 'User Training', type: 'task', startDate: '2026-10-18', endDate: '2026-10-26', duration: 9, percentComplete: 0, predecessors: '27SS+1d', successors: '29', resources: ['Maria Garcia'], allocation: [100] },
  { taskId: 29, wbs: '5.3', task: 'Hypercare Support', type: 'task', startDate: '2026-10-23', endDate: '2026-10-29', duration: 7, percentComplete: 0, predecessors: '27FS', successors: '30', resources: ['David Kim', 'James Chen', 'Robert Taylor'], allocation: [50, 50, 50] },
  { taskId: 30, wbs: '5.4', task: 'Project Closeout', type: 'milestone', startDate: '2026-10-30', endDate: '2026-10-30', duration: 1, percentComplete: 0, predecessors: '28FS,29FF', successors: '', resources: ['Sarah Johnson'], allocation: [100] },
];
const erpBaselineTasks: BaselineTask[] = [
  { taskId: 1, wbs: '1.0', task: 'Planning Phase', baselineStartDate: '2026-04-06', baselineEndDate: '2026-05-16', baselineDuration: 41, baselineCost: 0, baselinePredecessors: '', baselineResources: [] },
  { taskId: 2, wbs: '1.1', task: 'Project Charter Development', baselineStartDate: '2026-04-06', baselineEndDate: '2026-04-12', baselineDuration: 7, baselineCost: 0, baselinePredecessors: '', baselineResources: ['Sarah Johnson'] },
  { taskId: 3, wbs: '1.2', task: 'Stakeholder Analysis', baselineStartDate: '2026-04-13', baselineEndDate: '2026-04-18', baselineDuration: 6, baselineCost: 0, baselinePredecessors: '2FS', baselineResources: ['Sarah Johnson', 'Maria Garcia'] },
  { taskId: 4, wbs: '1.3', task: 'Requirements Gathering', baselineStartDate: '2026-04-20', baselineEndDate: '2026-05-05', baselineDuration: 16, baselineCost: 0, baselinePredecessors: '3FS', baselineResources: ['Maria Garcia', 'David Kim'] },
  { taskId: 5, wbs: '1.4', task: 'Project Plan Finalization', baselineStartDate: '2026-05-06', baselineEndDate: '2026-05-16', baselineDuration: 11, baselineCost: 0, baselinePredecessors: '4FS', baselineResources: ['Sarah Johnson'] },
  { taskId: 6, wbs: '2.0', task: 'Design Phase', baselineStartDate: '2026-05-17', baselineEndDate: '2026-07-19', baselineDuration: 64, baselineCost: 0, baselinePredecessors: '1FS', baselineResources: [] },
  { taskId: 7, wbs: '2.1', task: 'Current State Assessment', baselineStartDate: '2026-05-17', baselineEndDate: '2026-05-30', baselineDuration: 14, baselineCost: 0, baselinePredecessors: '5FS', baselineResources: ['Maria Garcia', 'Robert Taylor'] },
  { taskId: 8, wbs: '2.2', task: 'Solution Architecture Design', baselineStartDate: '2026-05-31', baselineEndDate: '2026-06-17', baselineDuration: 18, baselineCost: 0, baselinePredecessors: '7FS', baselineResources: ['David Kim'] },
  { taskId: 9, wbs: '2.3', task: 'Data Migration Strategy', baselineStartDate: '2026-06-18', baselineEndDate: '2026-07-05', baselineDuration: 18, baselineCost: 0, baselinePredecessors: '8FS', baselineResources: ['David Kim', 'Robert Taylor'] },
  { taskId: 10, wbs: '2.4', task: 'Interface Design', baselineStartDate: '2026-07-06', baselineEndDate: '2026-07-14', baselineDuration: 9, baselineCost: 0, baselinePredecessors: '9FS', baselineResources: ['Maria Garcia', 'James Chen'] },
  { taskId: 11, wbs: '2.5', task: 'Design Review & Approval', baselineStartDate: '2026-07-16', baselineEndDate: '2026-07-19', baselineDuration: 4, baselineCost: 0, baselinePredecessors: '10FS', baselineResources: ['Sarah Johnson', 'David Kim'] },
  { taskId: 12, wbs: '3.0', task: 'Build Phase', baselineStartDate: '2026-07-20', baselineEndDate: '2026-09-17', baselineDuration: 60, baselineCost: 0, baselinePredecessors: '6FS', baselineResources: [] },
  { taskId: 13, wbs: '3.1', task: 'Environment Setup', baselineStartDate: '2026-07-20', baselineEndDate: '2026-07-25', baselineDuration: 6, baselineCost: 0, baselinePredecessors: '11FS', baselineResources: ['David Kim', 'James Chen'] },
  { taskId: 14, wbs: '3.2', task: 'Finance Module Configuration', baselineStartDate: '2026-07-27', baselineEndDate: '2026-08-16', baselineDuration: 21, baselineCost: 0, baselinePredecessors: '13FS', baselineResources: ['James Chen', 'Robert Taylor'] },
  { taskId: 16, wbs: '3.4', task: 'Supply Chain Module Configuration', baselineStartDate: '2026-07-30', baselineEndDate: '2026-08-19', baselineDuration: 21, baselineCost: 0, baselinePredecessors: '13SS+2d', baselineResources: ['David Kim', 'James Chen'] },
  { taskId: 17, wbs: '3.5', task: 'Data Migration - Finance', baselineStartDate: '2026-08-18', baselineEndDate: '2026-08-23', baselineDuration: 6, baselineCost: 0, baselinePredecessors: '14FS', baselineResources: ['Robert Taylor', 'David Kim'] },
  { taskId: 19, wbs: '3.7', task: 'Data Migration - Supply Chain', baselineStartDate: '2026-08-21', baselineEndDate: '2026-08-21', baselineDuration: 1, baselineCost: 0, baselinePredecessors: '16FS', baselineResources: ['Robert Taylor', 'James Chen'] },
];
const cloudTasks: Task[] = [
  { taskId: 1, wbs: '1.0', task: 'Assessment & Planning', type: 'summary', startDate: '2026-02-02', endDate: '2026-03-15', duration: 42, percentComplete: 100, predecessors: '', successors: '', resources: [], allocation: [] },
  { taskId: 2, wbs: '1.1', task: 'Application Portfolio Assessment', type: 'task', startDate: '2026-02-02', endDate: '2026-02-21', duration: 20, percentComplete: 100, predecessors: '', successors: '3', resources: ['Aisha Patel', 'David Kim'], allocation: [100, 50] },
  { taskId: 3, wbs: '1.2', task: 'Cloud Readiness & TCO Analysis', type: 'task', startDate: '2026-02-22', endDate: '2026-03-07', duration: 14, percentComplete: 100, predecessors: '2FS', successors: '4', resources: ['Aisha Patel'], allocation: [100] },
  { taskId: 4, wbs: '1.3', task: 'Migration Strategy & Wave Planning', type: 'task', startDate: '2026-03-08', endDate: '2026-03-14', duration: 7, percentComplete: 100, predecessors: '3FS', successors: '5', resources: ['David Kim', 'Aisha Patel'], allocation: [100, 75] },
  { taskId: 5, wbs: '1.4', task: 'Assessment Sign-off', type: 'milestone', startDate: '2026-03-15', endDate: '2026-03-15', duration: 1, percentComplete: 100, predecessors: '4FS', successors: '7', resources: ['David Kim'], allocation: [100] },
  { taskId: 6, wbs: '2.0', task: 'Landing Zone & Foundation', type: 'summary', startDate: '2026-03-16', endDate: '2026-04-16', duration: 32, percentComplete: 100, predecessors: '1FS', successors: '', resources: [], allocation: [] },
  { taskId: 7, wbs: '2.1', task: 'AWS Landing Zone Setup', type: 'task', startDate: '2026-03-16', endDate: '2026-03-30', duration: 15, percentComplete: 100, predecessors: '5FS', successors: '8,9', resources: ['Carlos Mendez', 'Aisha Patel'], allocation: [100, 75] },
  { taskId: 8, wbs: '2.2', task: 'Network & Security Configuration', type: 'task', startDate: '2026-03-31', endDate: '2026-04-14', duration: 15, percentComplete: 100, predecessors: '7FS', successors: '10', resources: ['Carlos Mendez'], allocation: [100] },
  { taskId: 9, wbs: '2.3', task: 'CI/CD Pipeline Setup', type: 'task', startDate: '2026-03-31', endDate: '2026-04-12', duration: 13, percentComplete: 100, predecessors: '7SS', successors: '10', resources: ['Carlos Mendez', 'Nina Rossi'], allocation: [75, 100] },
  { taskId: 10, wbs: '2.4', task: 'Landing Zone Validation', type: 'milestone', startDate: '2026-04-15', endDate: '2026-04-16', duration: 2, percentComplete: 100, predecessors: '8FS,9FS', successors: '12', resources: ['Aisha Patel', 'Carlos Mendez'], allocation: [100, 100] },
  { taskId: 11, wbs: '3.0', task: 'Migration Wave 1', type: 'summary', startDate: '2026-04-17', endDate: '2026-06-22', duration: 67, percentComplete: 100, predecessors: '6FS', successors: '', resources: [], allocation: [] },
  { taskId: 12, wbs: '3.1', task: 'Wave 1 — Lift & Shift (30 apps)', type: 'task', startDate: '2026-04-17', endDate: '2026-05-16', duration: 30, percentComplete: 100, predecessors: '10FS', successors: '13', resources: ['Nina Rossi', 'Carlos Mendez'], allocation: [100, 75] },
  { taskId: 13, wbs: '3.2', task: 'Wave 1 — Database Migration', type: 'task', startDate: '2026-05-17', endDate: '2026-06-05', duration: 20, percentComplete: 100, predecessors: '12FS', successors: '14', resources: ['Robert Taylor', 'Aisha Patel'], allocation: [100, 50] },
  { taskId: 14, wbs: '3.3', task: 'Wave 1 — Testing & Validation', type: 'task', startDate: '2026-06-06', endDate: '2026-06-20', duration: 15, percentComplete: 100, predecessors: '13FS', successors: '15', resources: ['Carlos Mendez', 'David Kim'], allocation: [75, 50] },
  { taskId: 15, wbs: '3.4', task: 'Wave 1 Go-Live', type: 'milestone', startDate: '2026-06-21', endDate: '2026-06-22', duration: 2, percentComplete: 100, predecessors: '14FS', successors: '17', resources: ['Carlos Mendez'], allocation: [100] },
  { taskId: 16, wbs: '4.0', task: 'Migration Wave 2', type: 'summary', startDate: '2026-06-23', endDate: '2026-09-17', duration: 87, percentComplete: 35, predecessors: '11FS', successors: '', resources: [], allocation: [] },
  { taskId: 17, wbs: '4.1', task: 'Wave 2 — Refactor Applications', type: 'task', startDate: '2026-06-23', endDate: '2026-08-01', duration: 40, percentComplete: 80, predecessors: '15FS', successors: '18', resources: ['Aisha Patel', 'Nina Rossi'], allocation: [100, 100] },
  { taskId: 18, wbs: '4.2', task: 'Wave 2 — Database Migration', type: 'task', startDate: '2026-08-02', endDate: '2026-08-26', duration: 25, percentComplete: 0, predecessors: '17FS', successors: '19', resources: ['Robert Taylor'], allocation: [100] },
  { taskId: 19, wbs: '4.3', task: 'Wave 2 — Testing & Validation', type: 'task', startDate: '2026-08-27', endDate: '2026-09-17', duration: 22, percentComplete: 0, predecessors: '18FS', successors: '20', resources: ['Carlos Mendez', 'David Kim'], allocation: [75, 50] },
  { taskId: 20, wbs: '4.4', task: 'Wave 2 Go-Live', type: 'milestone', startDate: '2026-09-17', endDate: '2026-09-17', duration: 1, percentComplete: 0, predecessors: '19FS', successors: '22', resources: ['Carlos Mendez'], allocation: [100] },
  { taskId: 21, wbs: '5.0', task: 'Optimization & Cutover', type: 'summary', startDate: '2026-09-18', endDate: '2026-11-26', duration: 70, percentComplete: 0, predecessors: '16FS', successors: '', resources: [], allocation: [] },
  { taskId: 22, wbs: '5.1', task: 'Cost Optimization & Right-sizing', type: 'task', startDate: '2026-09-18', endDate: '2026-10-11', duration: 24, percentComplete: 0, predecessors: '20FS', successors: '23', resources: ['Aisha Patel'], allocation: [75] },
  { taskId: 23, wbs: '5.2', task: 'On-Prem Decommission', type: 'task', startDate: '2026-10-12', endDate: '2026-11-20', duration: 40, percentComplete: 0, predecessors: '22FS', successors: '24', resources: ['Carlos Mendez', 'Robert Taylor'], allocation: [100, 75] },
  { taskId: 24, wbs: '5.3', task: 'Project Closeout', type: 'milestone', startDate: '2026-11-25', endDate: '2026-11-26', duration: 2, percentComplete: 0, predecessors: '23FS', successors: '', resources: ['David Kim'], allocation: [100] },
];
const mobileTasks: Task[] = [
  { taskId: 1, wbs: '1.0', task: 'Discovery & UX Design', type: 'summary', startDate: '2026-03-02', endDate: '2026-04-24', duration: 54, percentComplete: 100, predecessors: '', successors: '', resources: [], allocation: [] },
  { taskId: 2, wbs: '1.1', task: 'Product Discovery & Requirements', type: 'task', startDate: '2026-03-02', endDate: '2026-03-21', duration: 20, percentComplete: 100, predecessors: '', successors: '3', resources: ['Jennifer Park', 'Maria Garcia'], allocation: [100, 75] },
  { taskId: 3, wbs: '1.2', task: 'UX Wireframes & User Flows', type: 'task', startDate: '2026-03-22', endDate: '2026-04-05', duration: 15, percentComplete: 100, predecessors: '2FS', successors: '4', resources: ['Maria Garcia'], allocation: [100] },
  { taskId: 4, wbs: '1.3', task: 'Visual Design & Prototype', type: 'task', startDate: '2026-04-06', endDate: '2026-04-24', duration: 19, percentComplete: 100, predecessors: '3FS', successors: '5', resources: ['Maria Garcia', 'Jennifer Park'], allocation: [100, 50] },
  { taskId: 5, wbs: '1.4', task: 'Design Sign-off', type: 'milestone', startDate: '2026-04-24', endDate: '2026-04-24', duration: 1, percentComplete: 100, predecessors: '4FS', successors: '7,11,12', resources: ['Jennifer Park'], allocation: [100] },
  { taskId: 6, wbs: '2.0', task: 'Backend & API Development', type: 'summary', startDate: '2026-04-25', endDate: '2026-08-02', duration: 100, percentComplete: 70, predecessors: '', successors: '', resources: [], allocation: [] },
  { taskId: 7, wbs: '2.1', task: 'API Architecture & Data Model', type: 'task', startDate: '2026-04-25', endDate: '2026-05-19', duration: 25, percentComplete: 100, predecessors: '5FS', successors: '8', resources: ['Leah Cohen', 'Tom Wilson'], allocation: [100, 50] },
  { taskId: 8, wbs: '2.2', task: 'Authentication & User Service', type: 'task', startDate: '2026-05-20', endDate: '2026-06-18', duration: 30, percentComplete: 100, predecessors: '7FS', successors: '9', resources: ['Leah Cohen'], allocation: [100] },
  { taskId: 9, wbs: '2.3', task: 'Core REST/GraphQL APIs', type: 'task', startDate: '2026-06-19', endDate: '2026-08-02', duration: 45, percentComplete: 35, predecessors: '8FS', successors: '13', resources: ['Leah Cohen', 'Anna Schmidt'], allocation: [100, 50] },
  { taskId: 10, wbs: '3.0', task: 'Mobile App Build', type: 'summary', startDate: '2026-05-16', endDate: '2026-11-01', duration: 170, percentComplete: 30, predecessors: '', successors: '', resources: [], allocation: [] },
  { taskId: 11, wbs: '3.1', task: 'iOS App Development', type: 'task', startDate: '2026-05-16', endDate: '2026-10-12', duration: 150, percentComplete: 35, predecessors: '5FS', successors: '14', resources: ['Tom Wilson'], allocation: [100] },
  { taskId: 12, wbs: '3.2', task: 'Android App Development', type: 'task', startDate: '2026-05-31', endDate: '2026-10-22', duration: 145, percentComplete: 25, predecessors: '5FS', successors: '14', resources: ['Anna Schmidt'], allocation: [100] },
  { taskId: 13, wbs: '3.3', task: 'Push Notifications & Offline Sync', type: 'task', startDate: '2026-09-18', endDate: '2026-10-31', duration: 44, percentComplete: 0, predecessors: '9FS', successors: '14', resources: ['Tom Wilson', 'Anna Schmidt'], allocation: [50, 50] },
  { taskId: 14, wbs: '3.4', task: 'Feature-Complete', type: 'milestone', startDate: '2026-11-01', endDate: '2026-11-01', duration: 1, percentComplete: 0, predecessors: '11FS,12FS,13FS', successors: '16', resources: ['Jennifer Park'], allocation: [100] },
  { taskId: 15, wbs: '4.0', task: 'QA & Launch', type: 'summary', startDate: '2026-11-02', endDate: '2027-01-31', duration: 91, percentComplete: 0, predecessors: '', successors: '', resources: [], allocation: [] },
  { taskId: 16, wbs: '4.1', task: 'QA & Device Testing', type: 'task', startDate: '2026-11-02', endDate: '2026-12-06', duration: 35, percentComplete: 0, predecessors: '14FS', successors: '17', resources: ['Kevin Nguyen', 'Tom Wilson', 'Anna Schmidt'], allocation: [100, 50, 50] },
  { taskId: 17, wbs: '4.2', task: 'Beta / TestFlight Program', type: 'task', startDate: '2026-12-07', endDate: '2026-12-31', duration: 25, percentComplete: 0, predecessors: '16FS', successors: '18', resources: ['Kevin Nguyen', 'Jennifer Park'], allocation: [100, 50] },
  { taskId: 18, wbs: '4.3', task: 'App Store & Play Submission', type: 'task', startDate: '2027-01-01', endDate: '2027-01-20', duration: 20, percentComplete: 0, predecessors: '17FS', successors: '19', resources: ['Tom Wilson', 'Anna Schmidt'], allocation: [50, 50] },
  { taskId: 19, wbs: '4.4', task: 'Public Launch', type: 'milestone', startDate: '2027-01-30', endDate: '2027-01-31', duration: 2, percentComplete: 0, predecessors: '18FS', successors: '', resources: ['Jennifer Park'], allocation: [100] },
];
const portalTasks: Task[] = [
  { taskId: 1, wbs: '1.0', task: 'Research & Discovery', type: 'summary', startDate: '2026-03-16', endDate: '2026-04-14', duration: 30, percentComplete: 100, predecessors: '', successors: '', resources: [], allocation: [] },
  { taskId: 2, wbs: '1.1', task: 'User Research & Journey Mapping', type: 'task', startDate: '2026-03-16', endDate: '2026-03-30', duration: 15, percentComplete: 100, predecessors: '', successors: '3', resources: ['Olivia Bennett', 'Rachel Kim'], allocation: [100, 50] },
  { taskId: 3, wbs: '1.2', task: 'IA & Content Audit', type: 'task', startDate: '2026-03-31', endDate: '2026-04-14', duration: 15, percentComplete: 100, predecessors: '2FS', successors: '5', resources: ['Olivia Bennett', 'Jordan Lee'], allocation: [75, 50] },
  { taskId: 4, wbs: '2.0', task: 'Design System', type: 'summary', startDate: '2026-04-15', endDate: '2026-05-29', duration: 45, percentComplete: 100, predecessors: '1FS', successors: '', resources: [], allocation: [] },
  { taskId: 5, wbs: '2.1', task: 'Design System Foundations', type: 'task', startDate: '2026-04-15', endDate: '2026-05-04', duration: 20, percentComplete: 100, predecessors: '3FS', successors: '6', resources: ['Jordan Lee'], allocation: [100] },
  { taskId: 6, wbs: '2.2', task: 'Component Library', type: 'task', startDate: '2026-05-05', endDate: '2026-05-29', duration: 25, percentComplete: 100, predecessors: '5FS', successors: '7', resources: ['Jordan Lee', 'Emma Davis'], allocation: [100, 75] },
  { taskId: 7, wbs: '2.3', task: 'Design System Sign-off', type: 'milestone', startDate: '2026-05-29', endDate: '2026-05-29', duration: 1, percentComplete: 100, predecessors: '6FS', successors: '9', resources: ['Rachel Kim', 'Jordan Lee'], allocation: [100, 100] },
  { taskId: 8, wbs: '3.0', task: 'Build & Migration', type: 'summary', startDate: '2026-05-30', endDate: '2026-08-27', duration: 90, percentComplete: 90, predecessors: '4FS', successors: '', resources: [], allocation: [] },
  { taskId: 9, wbs: '3.1', task: 'Portal Frontend Rebuild', type: 'task', startDate: '2026-05-30', endDate: '2026-07-13', duration: 45, percentComplete: 100, predecessors: '7FS', successors: '10', resources: ['Emma Davis', 'Jordan Lee'], allocation: [100, 50] },
  { taskId: 10, wbs: '3.2', task: 'Self-Service Workflow Rework', type: 'task', startDate: '2026-07-14', endDate: '2026-08-12', duration: 30, percentComplete: 100, predecessors: '9FS', successors: '11', resources: ['Emma Davis'], allocation: [100] },
  { taskId: 11, wbs: '3.3', task: 'Content Migration', type: 'task', startDate: '2026-08-13', endDate: '2026-08-27', duration: 15, percentComplete: 45, predecessors: '10FS', successors: '13', resources: ['Olivia Bennett', 'Rachel Kim'], allocation: [75, 50] },
  { taskId: 12, wbs: '4.0', task: 'Test & Launch', type: 'summary', startDate: '2026-08-28', endDate: '2026-09-29', duration: 33, percentComplete: 0, predecessors: '8FS', successors: '', resources: [], allocation: [] },
  { taskId: 13, wbs: '4.1', task: 'Accessibility & UAT', type: 'task', startDate: '2026-08-28', endDate: '2026-09-14', duration: 18, percentComplete: 0, predecessors: '11FS', successors: '14', resources: ['Emma Davis', 'Olivia Bennett'], allocation: [75, 75] },
  { taskId: 14, wbs: '4.2', task: 'Phased Rollout', type: 'task', startDate: '2026-09-15', endDate: '2026-09-26', duration: 12, percentComplete: 0, predecessors: '13FS', successors: '15', resources: ['Emma Davis', 'Rachel Kim'], allocation: [100, 50] },
  { taskId: 15, wbs: '4.3', task: 'Launch & Handover', type: 'milestone', startDate: '2026-09-28', endDate: '2026-09-29', duration: 2, percentComplete: 0, predecessors: '14FS', successors: '', resources: ['Rachel Kim'], allocation: [100] },
];
const analyticsTasks: Task[] = [
  { taskId: 1, wbs: '1.0', task: 'Foundation & Discovery', type: 'summary', startDate: '2026-06-15', endDate: '2026-08-03', duration: 50, percentComplete: 100, predecessors: '', successors: '', resources: [], allocation: [] },
  { taskId: 2, wbs: '1.1', task: 'Data Source Inventory & Requirements', type: 'task', startDate: '2026-06-15', endDate: '2026-07-04', duration: 20, percentComplete: 100, predecessors: '', successors: '3', resources: ['Marcus Chen', 'Sofia Reyes'], allocation: [100, 75] },
  { taskId: 3, wbs: '1.2', task: 'Snowflake Account & Warehouse Setup', type: 'task', startDate: '2026-07-05', endDate: '2026-07-19', duration: 15, percentComplete: 100, predecessors: '2FS', successors: '4', resources: ['Priya Singh'], allocation: [100] },
  { taskId: 4, wbs: '1.3', task: 'Data Governance & Security Model', type: 'task', startDate: '2026-07-20', endDate: '2026-08-03', duration: 15, percentComplete: 100, predecessors: '3FS', successors: '5', resources: ['Priya Singh', 'Marcus Chen'], allocation: [75, 50] },
  { taskId: 5, wbs: '1.4', task: 'Foundation Sign-off', type: 'milestone', startDate: '2026-08-03', endDate: '2026-08-03', duration: 1, percentComplete: 100, predecessors: '4FS', successors: '7', resources: ['Marcus Chen'], allocation: [100] },
  { taskId: 6, wbs: '2.0', task: 'Ingestion & Modeling', type: 'summary', startDate: '2026-08-04', endDate: '2026-12-01', duration: 120, percentComplete: 15, predecessors: '1FS', successors: '', resources: [], allocation: [] },
  { taskId: 7, wbs: '2.1', task: 'Source System Connectors (Fivetran)', type: 'task', startDate: '2026-08-04', endDate: '2026-09-02', duration: 30, percentComplete: 50, predecessors: '5FS', successors: '8', resources: ['Priya Singh', 'Robert Taylor'], allocation: [100, 75] },
  { taskId: 8, wbs: '2.2', task: 'dbt Staging & Core Models', type: 'task', startDate: '2026-09-03', endDate: '2026-10-17', duration: 45, percentComplete: 0, predecessors: '7FS', successors: '9', resources: ['Robert Taylor', 'Priya Singh'], allocation: [100, 50] },
  { taskId: 9, wbs: '2.3', task: 'Data Quality & Testing Framework', type: 'task', startDate: '2026-10-18', endDate: '2026-11-11', duration: 25, percentComplete: 0, predecessors: '8FS', successors: '10', resources: ['Robert Taylor'], allocation: [100] },
  { taskId: 10, wbs: '2.4', task: 'Semantic Layer (Looker)', type: 'task', startDate: '2026-11-12', endDate: '2026-12-01', duration: 20, percentComplete: 0, predecessors: '9FS', successors: '12', resources: ['Oliver Brown', 'Sofia Reyes'], allocation: [100, 50] },
  { taskId: 11, wbs: '3.0', task: 'Self-Service BI', type: 'summary', startDate: '2026-12-02', endDate: '2027-03-16', duration: 105, percentComplete: 0, predecessors: '6FS', successors: '', resources: [], allocation: [] },
  { taskId: 12, wbs: '3.1', task: 'Executive Dashboards', type: 'task', startDate: '2026-12-02', endDate: '2027-01-05', duration: 35, percentComplete: 0, predecessors: '10FS', successors: '13', resources: ['Oliver Brown', 'Sofia Reyes'], allocation: [100, 75] },
  { taskId: 13, wbs: '3.2', task: 'Self-Service Explores & Training', type: 'task', startDate: '2027-01-06', endDate: '2027-02-14', duration: 40, percentComplete: 0, predecessors: '12FS', successors: '14', resources: ['Sofia Reyes', 'Oliver Brown'], allocation: [100, 75] },
  { taskId: 14, wbs: '3.3', task: 'UAT & Data Validation', type: 'task', startDate: '2027-02-15', endDate: '2027-03-16', duration: 30, percentComplete: 0, predecessors: '13FS', successors: '15', resources: ['Marcus Chen', 'Sofia Reyes'], allocation: [75, 75] },
  { taskId: 15, wbs: '3.4', task: 'Platform Go-Live', type: 'milestone', startDate: '2027-03-16', endDate: '2027-03-16', duration: 1, percentComplete: 0, predecessors: '14FS', successors: '17', resources: ['Marcus Chen'], allocation: [100] },
  { taskId: 16, wbs: '4.0', task: 'Adoption & Optimization', type: 'summary', startDate: '2027-03-17', endDate: '2027-05-30', duration: 75, percentComplete: 0, predecessors: '11FS', successors: '', resources: [], allocation: [] },
  { taskId: 17, wbs: '4.1', task: 'Business User Enablement', type: 'task', startDate: '2027-03-17', endDate: '2027-04-25', duration: 40, percentComplete: 0, predecessors: '15FS', successors: '18', resources: ['Sofia Reyes'], allocation: [100] },
  { taskId: 18, wbs: '4.2', task: 'Performance Tuning & Cost Controls', type: 'task', startDate: '2027-04-26', endDate: '2027-05-25', duration: 30, percentComplete: 0, predecessors: '17FS', successors: '19', resources: ['Priya Singh', 'Robert Taylor'], allocation: [75, 75] },
  { taskId: 19, wbs: '4.3', task: 'Project Closeout', type: 'milestone', startDate: '2027-05-29', endDate: '2027-05-29', duration: 1, percentComplete: 0, predecessors: '18FS', successors: '', resources: ['Marcus Chen'], allocation: [100] },
];
const securityTasks: Task[] = [
  { taskId: 1, wbs: '1.0', task: 'Assessment & Roadmap', type: 'summary', startDate: '2026-04-01', endDate: '2026-05-10', duration: 40, percentComplete: 100, predecessors: '', successors: '', resources: [], allocation: [] },
  { taskId: 2, wbs: '1.1', task: 'Security Posture Assessment', type: 'task', startDate: '2026-04-01', endDate: '2026-04-20', duration: 20, percentComplete: 100, predecessors: '', successors: '3', resources: ['Sam Patel', 'Daniel Foster'], allocation: [100, 75] },
  { taskId: 3, wbs: '1.2', task: 'Gap Analysis & SOC 2 Readiness', type: 'task', startDate: '2026-04-21', endDate: '2026-05-05', duration: 15, percentComplete: 100, predecessors: '2FS', successors: '4', resources: ['Grace Liu', 'Sam Patel'], allocation: [100, 50] },
  { taskId: 4, wbs: '1.3', task: 'Zero-Trust Roadmap', type: 'task', startDate: '2026-05-06', endDate: '2026-05-10', duration: 5, percentComplete: 100, predecessors: '3FS', successors: '5', resources: ['Sam Patel', 'Alex Brennan'], allocation: [100, 50] },
  { taskId: 5, wbs: '1.4', task: 'Roadmap Approval', type: 'milestone', startDate: '2026-05-11', endDate: '2026-05-11', duration: 1, percentComplete: 100, predecessors: '4FS', successors: '7', resources: ['Alex Brennan'], allocation: [100] },
  { taskId: 6, wbs: '2.0', task: 'Identity & Access', type: 'summary', startDate: '2026-05-12', endDate: '2026-07-10', duration: 60, percentComplete: 100, predecessors: '1FS', successors: '', resources: [], allocation: [] },
  { taskId: 7, wbs: '2.1', task: 'MFA Rollout (All Users)', type: 'task', startDate: '2026-05-12', endDate: '2026-06-15', duration: 35, percentComplete: 100, predecessors: '5FS', successors: '8', resources: ['Daniel Foster', 'Sam Patel'], allocation: [100, 50] },
  { taskId: 8, wbs: '2.2', task: 'Privileged Access Management', type: 'task', startDate: '2026-06-16', endDate: '2026-07-10', duration: 25, percentComplete: 100, predecessors: '7FS', successors: '9', resources: ['Daniel Foster'], allocation: [100] },
  { taskId: 9, wbs: '2.3', task: 'MFA Coverage Milestone', type: 'milestone', startDate: '2026-07-10', endDate: '2026-07-10', duration: 1, percentComplete: 100, predecessors: '8FS', successors: '11', resources: ['Alex Brennan'], allocation: [100] },
  { taskId: 10, wbs: '3.0', task: 'Network & Detection', type: 'summary', startDate: '2026-07-11', endDate: '2026-10-08', duration: 90, percentComplete: 45, predecessors: '6FS', successors: '', resources: [], allocation: [] },
  { taskId: 11, wbs: '3.1', task: 'Zero-Trust Network Segmentation', type: 'task', startDate: '2026-07-11', endDate: '2026-08-24', duration: 45, percentComplete: 90, predecessors: '9FS', successors: '12', resources: ['Sam Patel', 'Daniel Foster'], allocation: [100, 75] },
  { taskId: 12, wbs: '3.2', task: 'SIEM Upgrade & Log Onboarding', type: 'task', startDate: '2026-08-25', endDate: '2026-09-23', duration: 30, percentComplete: 0, predecessors: '11FS', successors: '13', resources: ['Daniel Foster', 'Sam Patel'], allocation: [100, 50] },
  { taskId: 13, wbs: '3.3', task: 'SOC Playbooks & Detection Rules', type: 'task', startDate: '2026-09-24', endDate: '2026-10-08', duration: 15, percentComplete: 0, predecessors: '12FS', successors: '15', resources: ['Daniel Foster'], allocation: [100] },
  { taskId: 14, wbs: '4.0', task: 'Compliance & Audit', type: 'summary', startDate: '2026-10-09', endDate: '2027-01-29', duration: 113, percentComplete: 0, predecessors: '10FS', successors: '', resources: [], allocation: [] },
  { taskId: 15, wbs: '4.1', task: 'Policy & Control Documentation', type: 'task', startDate: '2026-10-09', endDate: '2026-11-17', duration: 40, percentComplete: 0, predecessors: '13FS', successors: '16', resources: ['Grace Liu'], allocation: [100] },
  { taskId: 16, wbs: '4.2', task: 'Penetration Testing & Remediation', type: 'task', startDate: '2026-11-18', endDate: '2026-12-22', duration: 35, percentComplete: 0, predecessors: '15FS', successors: '17', resources: ['Sam Patel', 'Daniel Foster'], allocation: [100, 75] },
  { taskId: 17, wbs: '4.3', task: 'SOC 2 Type II Audit Window', type: 'task', startDate: '2026-12-23', endDate: '2027-01-21', duration: 30, percentComplete: 0, predecessors: '16FS', successors: '18', resources: ['Grace Liu', 'Alex Brennan'], allocation: [100, 50] },
  { taskId: 18, wbs: '4.4', task: 'SOC 2 Attestation', type: 'milestone', startDate: '2027-01-29', endDate: '2027-01-29', duration: 1, percentComplete: 0, predecessors: '17FS', successors: '', resources: ['Alex Brennan'], allocation: [100] },
];

// -----------------------------------------------------------------------------
// Portfolios & Programs (Phase 42 demo). Loaded on first run in localStorage
// mode via seed-fallback in useProjectStore.bootstrap(), mirroring seedProjects.
// -----------------------------------------------------------------------------
export const seedPortfolios: Portfolio[] = [
  {
    id: 'PORT-001',
    name: 'Digital Transformation',
    description: 'Enterprise-wide modernization of core platforms and data capabilities to cut cost, improve visibility, and enable real-time decisioning.',
    manager: 'Michael Torres, Chief Operating Officer',
    status: 'active',
    startDate: '2026-01-01',
    endDate: '2027-06-30',
    targetBudget: 6_000_000,
    createdAt: '2026-01-05T09:00:00.000Z',
    updatedAt: '2026-08-01T09:00:00.000Z',
  },
  {
    id: 'PORT-002',
    name: 'Customer Experience',
    description: 'Customer-facing digital products — mobile and web — that grow engagement, raise NPS, and reduce support load.',
    manager: 'Diana Foster, VP Customer Experience',
    status: 'active',
    startDate: '2026-01-01',
    endDate: '2027-03-31',
    targetBudget: 1_500_000,
    createdAt: '2026-01-05T09:00:00.000Z',
    updatedAt: '2026-08-01T09:00:00.000Z',
  },
  {
    id: 'PORT-003',
    name: 'Enterprise Platforms',
    description: 'Security, compliance, and shared platform investments that underpin the enterprise and its audit posture.',
    manager: 'Lisa Anderson, Chief Technology Officer',
    status: 'active',
    startDate: '2026-01-01',
    endDate: '2027-03-31',
    targetBudget: 1_200_000,
    createdAt: '2026-01-05T09:00:00.000Z',
    updatedAt: '2026-08-01T09:00:00.000Z',
  },
];

export const seedPrograms: Program[] = [
  {
    id: 'PROG-001',
    name: 'Core Systems Modernization',
    description: 'ERP, cloud migration, and the analytics platform — the backbone systems being modernized together.',
    manager: 'Sarah Johnson',
    status: 'active',
    startDate: '2026-01-01',
    endDate: '2027-06-30',
    targetBudget: 5_400_000,
    portfolioId: 'PORT-001',
    createdAt: '2026-01-05T09:00:00.000Z',
    updatedAt: '2026-08-01T09:00:00.000Z',
  },
  {
    id: 'PROG-002',
    name: 'Customer-Facing Apps',
    description: 'The mobile app and customer portal redesign delivering the customer-facing experience roadmap.',
    manager: 'Jennifer Park',
    status: 'active',
    startDate: '2026-01-01',
    endDate: '2027-03-31',
    targetBudget: 1_270_000,
    portfolioId: 'PORT-002',
    createdAt: '2026-01-05T09:00:00.000Z',
    updatedAt: '2026-08-01T09:00:00.000Z',
  },
  {
    id: 'PROG-003',
    name: 'Security & Compliance',
    description: 'Zero-trust, MFA, SIEM, and SOC 2 workstreams hardening the enterprise security posture.',
    manager: 'Alex Brennan',
    status: 'active',
    startDate: '2026-04-01',
    endDate: '2027-03-31',
    targetBudget: 950_000,
    portfolioId: 'PORT-003',
    createdAt: '2026-04-01T09:00:00.000Z',
    updatedAt: '2026-08-01T09:00:00.000Z',
  },
];

/**
 * Sample seed projects matching the original ProjexHQ monolith.
 */
export const seedProjects: Project[] = [
  {
    id: 'PRJ-001',
    name: 'ERP Implementation',
    description:
      'Enterprise-wide ERP system implementation to streamline business processes, improve data visibility, and reduce operational costs. Includes modules for Finance, HR, Supply Chain, and Customer Management.',
    status: 'on-track',
    health: 'healthy',
    priority: 'critical',
    methodology: 'Waterfall',
    portfolioId: 'PORT-001',
    programId: 'PROG-001',
    startDate: '2026-04-06',
    endDate: '2026-10-30',
    completion: 68,
    budget: 2_400_000,
    spent: 1_620_000,
    cpi: 0.96,
    spi: 1.04,
    pm: 'Sarah Johnson',
    sponsor: 'Michael Torres, Chief Operating Officer',
    teamSize: 12,
    team: [
      { name: 'Sarah Johnson', role: 'Project Manager', allocation: 100 },
      { name: 'David Kim', role: 'Tech Lead', allocation: 100 },
      { name: 'Maria Garcia', role: 'Business Analyst', allocation: 75 },
      { name: 'James Chen', role: 'Solution Architect', allocation: 100 },
      { name: 'Robert Taylor', role: 'Data Engineer', allocation: 100 },
    ],
    scope: 'Implementation of SAP S/4HANA across Finance, HR, Supply Chain, and Customer Management modules.',
    objectives: [
      'Reduce operational costs by 20%',
      'Improve data accuracy to 99.5%',
      'Streamline financial close from 10 days to 3 days',
      'Enable real-time reporting and analytics',
    ],
    phases: [
      { name: 'Planning', start: '2026-04-06', end: '2026-05-16', status: 'completed', completion: 100 },
      { name: 'Design', start: '2026-05-17', end: '2026-07-19', status: 'completed', completion: 100 },
      { name: 'Build', start: '2026-07-20', end: '2026-09-17', status: 'in-progress', completion: 55 },
      { name: 'Testing', start: '2026-09-19', end: '2026-10-15', status: 'not-started', completion: 0 },
      { name: 'Deployment', start: '2026-10-16', end: '2026-10-30', status: 'not-started', completion: 0 },
    ],
    wbsTasks: erpTasks,
    risks: [
      {
        id: 'RSK-001',
        title: 'Legacy data quality issues during migration',
        description: 'Source system data has known inconsistencies that may delay migration phases.',
        category: 'Technical',
        probability: 70,
        impact: 'high',
        exposure: 180_000,
        status: 'open',
        owner: 'Robert Taylor',
        responseStrategy: 'mitigate',
        mitigation: 'Run data profiling early, build cleansing scripts, allocate buffer time in migration tasks.',
        identifiedDate: '2026-04-22',
      },
      {
        id: 'RSK-002',
        title: 'Key SME unavailable during UAT',
        description: 'Finance department lead has scheduled vacation overlapping with planned UAT window.',
        category: 'Resource',
        probability: 50,
        impact: 'medium',
        exposure: 60_000,
        status: 'mitigated',
        owner: 'Sarah Johnson',
        responseStrategy: 'avoid',
        mitigation: 'Identified backup SME and pre-recorded knowledge transfer sessions.',
        identifiedDate: '2026-06-20',
      },
      {
        id: 'RSK-003',
        title: 'Integration with legacy CRM may exceed scope',
        description: 'Newly discovered API limitations in legacy CRM may require additional middleware.',
        category: 'Technical',
        probability: 40,
        impact: 'critical',
        exposure: 250_000,
        status: 'open',
        owner: 'David Kim',
        responseStrategy: 'mitigate',
        mitigation: 'POC scheduled for week of 2026-08-24; CR may follow if confirmed.',
        identifiedDate: '2026-07-05',
      },
      {
        id: 'RSK-004',
        title: 'User adoption resistance from operations team',
        description: 'Change management feedback indicates concerns about new workflows.',
        category: 'Organizational',
        probability: 60,
        impact: 'medium',
        exposure: 80_000,
        status: 'monitoring',
        owner: 'Maria Garcia',
        responseStrategy: 'mitigate',
        mitigation: 'Expanded training plan, executive sponsor town halls, change champion network.',
        identifiedDate: '2026-05-05',
      },
      {
        id: 'RSK-005',
        title: 'Cloud infrastructure cost overrun',
        description: 'Initial sizing estimates may be insufficient for production load patterns.',
        category: 'Financial',
        probability: 30,
        impact: 'high',
        exposure: 120_000,
        status: 'closed',
        owner: 'James Chen',
        responseStrategy: 'transfer',
        mitigation: 'Negotiated reserved instance pricing; capacity sized at p99 + 30% buffer.',
        identifiedDate: '2026-05-20',
      },
    ],
    issues: [
      {
        id: 'ISS-001',
        title: 'Sandbox environment instability',
        description: 'Test environment has been intermittently unavailable during configuration phase.',
        status: 'in-progress',
        severity: 'medium',
        owner: 'James Chen',
        raisedDate: '2026-07-22',
        dueDate: '2026-09-05',
      },
      {
        id: 'ISS-002',
        title: 'Reporting requirements not fully signed off',
        description: 'Finance has not formally approved the new report catalog from design phase.',
        status: 'open',
        severity: 'high',
        owner: 'Maria Garcia',
        raisedDate: '2026-08-05',
        dueDate: '2026-09-12',
      },
    ],
    changes: [
      {
        id: 'CR-001',
        title: 'Reduce mobile app scope to phase 2',
        type: 'scope',
        status: 'rejected',
        requestedBy: 'Operations',
        requestDate: '2026-04-25',
        impact: 'Defer mobile capability; saves $80K, reduces value to operations.',
        costImpact: -80_000,
        priority: 'low',
        justification: 'Operations indicated mobile is nice-to-have for v1.',
        decision: 'Rejected — mobile is required by sponsor.',
      },
      {
        id: 'CR-002',
        title: 'Add additional UAT cycle',
        type: 'schedule',
        status: 'approved',
        requestedBy: 'Maria Garcia',
        requestDate: '2026-05-02',
        impact: 'Adds 5 days to schedule; improves quality at deployment.',
        costImpact: 25_000,
        scheduleImpact: 5,
        priority: 'medium',
        justification: 'Risk of post-launch defects without additional regression coverage.',
        decision: 'Approved by sponsor 2026-05-08.',
      },
      {
        id: 'CR-003',
        title: 'Add Customer Management module to scope',
        type: 'scope',
        status: 'implemented',
        requestedBy: 'Michael Torres',
        requestDate: '2026-05-28',
        impact: 'Major scope expansion; +$200K budget, +30 days schedule, +50 users in training.',
        costImpact: 200_000,
        scheduleImpact: 30,
        priority: 'critical',
        justification: 'Strategic alignment with customer experience initiative; ROI confirmed.',
        decision: 'Approved by steering committee; baseline reset to BL-ERP-002.',
      },
      {
        id: 'CR-004',
        title: 'Replace HR reporting tool with native module',
        type: 'scope',
        status: 'review',
        requestedBy: 'James Chen',
        requestDate: '2026-08-03',
        impact: 'Eliminates third-party tool, simplifies architecture; some custom report rework.',
        costImpact: -45_000,
        priority: 'medium',
        justification: 'Native module discovered during build; better long-term maintainability.',
        decision: '',
      },
    ],
    decisions: [
      {
        id: 'DEC-101',
        title: 'Adopt SAP S/4HANA public cloud edition',
        context: 'Choice between on-prem, private, and public cloud editions given IT strategy and TCO targets.',
        decision: 'Standardize on the public cloud edition to reduce infrastructure overhead and accelerate upgrades.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'James Chen',
        decisionDate: '2026-05-20',
        flaggedForSteering: true,
      },
      {
        id: 'DEC-102',
        title: 'Sequence Customer Management module after core go-live',
        context: 'CR-003 added Customer Management; delivering it in the main wave risked the core timeline.',
        decision: 'Deliver Finance/HR/Supply Chain first, then Customer Management as a fast-follow.',
        category: 'Scope',
        status: 'decided',
        decidedBy: 'Sarah Johnson',
        decisionDate: '2026-05-28',
        flaggedForSteering: true,
      },
      {
        id: 'DEC-103',
        title: 'Standardize on Fiori UX across all modules',
        context: 'Mixed legacy GUIs increased training burden and support tickets.',
        decision: 'Use SAP Fiori as the single UX layer for all in-scope modules.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'David Kim',
        decisionDate: '2026-06-10',
        flaggedForSteering: false,
      },
    ],
    actionItems: [
      {
        id: 'ACT-101',
        title: 'Finalize UAT test scripts with Finance',
        description: 'Confirm end-to-end financial close scenarios and sign-off criteria with the Finance SME.',
        owner: 'Maria Garcia',
        dueDate: '2026-09-10',
        status: 'open',
        flaggedForSteering: true,
      },
      {
        id: 'ACT-102',
        title: 'Complete Supply Chain data reconciliation',
        owner: 'Robert Taylor',
        dueDate: '2026-08-28',
        status: 'in-progress',
        flaggedForSteering: false,
      },
      {
        id: 'ACT-103',
        title: 'Confirm integration architecture with CRM vendor',
        owner: 'David Kim',
        dueDate: '2026-08-30',
        status: 'open',
        flaggedForSteering: true,
      },
      {
        id: 'ACT-104',
        title: 'Schedule executive go-live readiness review',
        owner: 'Sarah Johnson',
        dueDate: '2026-10-05',
        status: 'open',
        flaggedForSteering: false,
      },
    ],
    activeBaselineId: 'BL-ERP-002',
    baselines: [
      {
        id: 'BL-ERP-001',
        name: 'Initial Baseline',
        type: 'initial',
        setDate: '2026-04-06T09:00:00.000Z',
        setBy: 'Sarah Johnson',
        reason: 'Initial approved plan per project charter v1.0, signed off by steering committee.',
        linkedChangeRequest: null,
        approvedBy: 'Steering Committee',
        status: 'superseded',
        snapshot: {
          budget: 2_200_000,
          startDate: '2026-04-06',
          endDate: '2026-09-17',
          scope: 'Implementation of SAP S/4HANA across Finance, HR, and Supply Chain modules.',
          objectives: [
            'Reduce operational costs by 20%',
            'Improve data accuracy to 99.5%',
            'Streamline financial close from 10 days to 3 days',
          ],
          completion: 0,
          wbsTasks: [],
          taskCount: 0,
        },
      },
      {
        id: 'BL-ERP-002',
        name: 'Q2 Re-baseline (CR-003)',
        type: 'rebaseline',
        setDate: '2026-06-05T14:30:00.000Z',
        setBy: 'Sarah Johnson',
        reason: 'Approved CR-003 added Customer Management module to scope. Budget increased by $200K, end date extended.',
        linkedChangeRequest: 'CR-003',
        approvedBy: 'Michael Torres (Sponsor)',
        status: 'active',
        snapshot: {
          budget: 2_400_000,
          startDate: '2026-04-06',
          endDate: '2026-10-30',
          scope: 'Implementation of SAP S/4HANA across Finance, HR, Supply Chain, and Customer Management modules.',
          objectives: [
            'Reduce operational costs by 20%',
            'Improve data accuracy to 99.5%',
            'Streamline financial close from 10 days to 3 days',
            'Enable real-time reporting and analytics',
          ],
          completion: 35,
          wbsTasks: erpBaselineTasks,
          taskCount: erpBaselineTasks.length,
        },
      },
    ],
    tags: ['enterprise', 'sap', 'transformation'],
  },
  {
    id: 'PRJ-002',
    name: 'Cloud Migration',
    description:
      'Migrate on-premise infrastructure to AWS cloud, including 200+ applications, databases, and services.',
    status: 'at-risk',
    health: 'watch',
    priority: 'high',
    methodology: 'Hybrid',
    portfolioId: 'PORT-001',
    programId: 'PROG-001',
    startDate: '2026-02-02',
    endDate: '2026-11-26',
    completion: 60,
    budget: 1_800_000,
    spent: 1_140_000,
    cpi: 1.02,
    spi: 0.94,
    pm: 'David Kim',
    sponsor: 'Lisa Anderson, CTO',
    teamSize: 8,
    team: [
      { name: 'David Kim', role: 'Project Manager', allocation: 100 },
      { name: 'Aisha Patel', role: 'Cloud Architect', allocation: 100 },
      { name: 'Carlos Mendez', role: 'DevOps Lead', allocation: 100 },
      { name: 'Nina Rossi', role: 'Migration Engineer', allocation: 100 },
      { name: 'Robert Taylor', role: 'Data Engineer', allocation: 75 },
    ],
    scope: 'Migrate workloads to AWS, set up landing zones, refactor as needed.',
    objectives: ['Reduce data center costs by 35%', 'Improve scalability and elasticity', 'Enable global deployment'],
    wbsTasks: cloudTasks,
    risks: [
      {
        id: 'RSK-201',
        title: 'Wave 2 refactor complexity underestimated',
        description: 'Several legacy monoliths require deeper re-architecture than the assessment assumed.',
        category: 'Technical',
        probability: 65,
        impact: 'high',
        exposure: 220_000,
        status: 'open',
        owner: 'Aisha Patel',
        responseStrategy: 'mitigate',
        mitigation: 'Break refactor into smaller app groups; add contractor capacity; weekly architecture review.',
        identifiedDate: '2026-06-25',
      },
      {
        id: 'RSK-202',
        title: 'Data egress and transfer costs exceed budget',
        description: 'Large dataset transfers to AWS may incur higher-than-planned egress charges.',
        category: 'Financial',
        probability: 55,
        impact: 'medium',
        exposure: 90_000,
        status: 'monitoring',
        owner: 'David Kim',
        responseStrategy: 'mitigate',
        mitigation: 'Schedule bulk transfers off-peak; use Snowball for large datasets; monitor egress dashboards.',
        identifiedDate: '2026-04-10',
      },
      {
        id: 'RSK-203',
        title: 'Extended parallel-run doubles infrastructure cost',
        description: 'Running on-prem and cloud in parallel longer than planned inflates run-rate.',
        category: 'Financial',
        probability: 60,
        impact: 'high',
        exposure: 140_000,
        status: 'open',
        owner: 'Carlos Mendez',
        responseStrategy: 'mitigate',
        mitigation: 'Tighten cutover criteria; time-box parallel run to two weeks per wave.',
        identifiedDate: '2026-07-15',
      },
      {
        id: 'RSK-204',
        title: 'Key DevOps engineer attrition risk',
        description: 'Single point of knowledge on the landing-zone automation.',
        category: 'Resource',
        probability: 35,
        impact: 'high',
        exposure: 110_000,
        status: 'mitigated',
        owner: 'David Kim',
        responseStrategy: 'avoid',
        mitigation: 'Cross-train two engineers; document runbooks; retention bonus approved.',
        identifiedDate: '2026-05-01',
      },
    ],
    issues: [
      {
        id: 'ISS-201',
        title: 'Legacy DB replication lag blocking Wave 2 cutover',
        description: 'Change data capture from the legacy Oracle DB is lagging beyond the cutover tolerance.',
        status: 'open',
        severity: 'high',
        owner: 'Robert Taylor',
        raisedDate: '2026-08-04',
        dueDate: '2026-09-01',
      },
      {
        id: 'ISS-202',
        title: 'IAM role sprawl slowing landing-zone validation',
        description: 'Inconsistent IAM policies across accounts are failing guardrail checks.',
        status: 'in-progress',
        severity: 'medium',
        owner: 'Carlos Mendez',
        raisedDate: '2026-07-10',
        dueDate: '2026-08-25',
      },
    ],
    changes: [
      {
        id: 'CR-201',
        title: 'Add second AWS region for disaster recovery',
        type: 'scope',
        status: 'approved',
        requestedBy: 'Lisa Anderson',
        requestDate: '2026-06-10',
        impact: 'Adds cross-region resilience; +$120K, +15 days.',
        costImpact: 120_000,
        scheduleImpact: 15,
        priority: 'high',
        justification: 'Compliance requires cross-region DR for tier-1 systems.',
        decision: 'Approved by CTO 2026-06-18.',
      },
      {
        id: 'CR-202',
        title: 'Defer 12 low-priority Wave 2 apps to next FY',
        type: 'schedule',
        status: 'review',
        requestedBy: 'David Kim',
        requestDate: '2026-08-02',
        impact: 'Reduces near-term delivery risk; moves 12 apps to next fiscal year.',
        costImpact: -60_000,
        scheduleImpact: -20,
        priority: 'medium',
        justification: 'Wave 2 complexity is threatening the go-live date.',
        decision: '',
      },
      {
        id: 'CR-203',
        title: 'Adopt reserved instances for steady-state workloads',
        type: 'budget',
        status: 'implemented',
        requestedBy: 'Aisha Patel',
        requestDate: '2026-05-05',
        impact: 'Lowers steady-state run-rate ~28%.',
        costImpact: -75_000,
        priority: 'medium',
        justification: 'Right-sizing analysis complete; usage patterns are stable.',
        decision: 'Approved and applied.',
      },
    ],
    decisions: [
      {
        id: 'DEC-201',
        title: 'Standardize on Terraform for all infrastructure-as-code',
        decision: 'Use Terraform (over CloudFormation) as the single IaC standard across all accounts.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'Carlos Mendez',
        decisionDate: '2026-03-20',
        flaggedForSteering: false,
      },
      {
        id: 'DEC-202',
        title: 'Adopt hub-and-spoke landing zone topology',
        decision: 'Centralize shared services in a hub account with spoke accounts per workload domain.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'Aisha Patel',
        decisionDate: '2026-04-05',
        flaggedForSteering: false,
      },
      {
        id: 'DEC-203',
        title: 'Pause Wave 2 pending refactor re-scoping',
        context: 'Wave 2 complexity is the top schedule risk; proceeding as-is endangers go-live.',
        decision: 'Hold Wave 2 kickoff until the re-scoping proposal (CR-202) is decided.',
        category: 'Scope',
        status: 'proposed',
        decidedBy: 'David Kim',
        decisionDate: '2026-08-05',
        flaggedForSteering: true,
      },
    ],
    actionItems: [
      {
        id: 'ACT-201',
        title: 'Finalize Wave 2 app re-scoping proposal',
        owner: 'David Kim',
        dueDate: '2026-08-28',
        status: 'in-progress',
        flaggedForSteering: true,
      },
      {
        id: 'ACT-202',
        title: 'Resolve DB replication lag with database vendor',
        owner: 'Robert Taylor',
        dueDate: '2026-09-01',
        status: 'open',
        flaggedForSteering: true,
      },
      {
        id: 'ACT-203',
        title: 'Publish cloud cost-optimization dashboard',
        owner: 'Aisha Patel',
        dueDate: '2026-09-15',
        status: 'open',
        flaggedForSteering: false,
      },
    ],
    baselines: [],
    activeBaselineId: null,
    tags: ['cloud', 'aws', 'infrastructure'],
  },
  {
    id: 'PRJ-003',
    name: 'Mobile App Development',
    description: 'Native iOS and Android customer app with real-time features, push notifications, and offline mode.',
    status: 'critical',
    health: 'at-risk',
    priority: 'high',
    methodology: 'Agile',
    portfolioId: 'PORT-002',
    programId: 'PROG-002',
    startDate: '2026-03-02',
    endDate: '2027-01-31',
    completion: 40,
    budget: 850_000,
    spent: 470_000,
    cpi: 0.82,
    spi: 0.78,
    pm: 'Jennifer Park',
    sponsor: 'Mark Rodriguez, VP Product',
    teamSize: 10,
    team: [
      { name: 'Jennifer Park', role: 'Product Manager', allocation: 100 },
      { name: 'Tom Wilson', role: 'iOS Lead', allocation: 100 },
      { name: 'Anna Schmidt', role: 'Android Lead', allocation: 100 },
      { name: 'Leah Cohen', role: 'Backend Engineer', allocation: 100 },
      { name: 'Kevin Nguyen', role: 'QA Lead', allocation: 100 },
      { name: 'Maria Garcia', role: 'UX / Business Analyst', allocation: 75 },
    ],
    scope: 'Native iOS/Android app, backend APIs, push notifications, offline sync.',
    objectives: ['Launch in App Store and Google Play', 'Achieve 100K downloads in 6 months', '4.5+ star rating'],
    wbsTasks: mobileTasks,
    risks: [
      {
        id: 'RSK-301',
        title: 'iOS and Android feature parity slipping',
        description: 'Platform teams are diverging on feature scope and timing.',
        category: 'Technical',
        probability: 75,
        impact: 'high',
        exposure: 130_000,
        status: 'open',
        owner: 'Tom Wilson',
        responseStrategy: 'mitigate',
        mitigation: 'Freeze scope; maintain a shared feature checklist; daily cross-platform sync.',
        identifiedDate: '2026-06-05',
      },
      {
        id: 'RSK-302',
        title: 'Third-party push provider reliability',
        description: 'Push delivery rates from the current provider are inconsistent under load.',
        category: 'Technical',
        probability: 45,
        impact: 'medium',
        exposure: 40_000,
        status: 'monitoring',
        owner: 'Leah Cohen',
        responseStrategy: 'mitigate',
        mitigation: 'Evaluate a fallback provider; add a retry/queue layer.',
        identifiedDate: '2026-05-20',
      },
      {
        id: 'RSK-303',
        title: 'App Store review rejection risk',
        description: 'Guideline ambiguity around account handling could trigger rejection.',
        category: 'Organizational',
        probability: 50,
        impact: 'high',
        exposure: 70_000,
        status: 'open',
        owner: 'Jennifer Park',
        responseStrategy: 'mitigate',
        mitigation: 'Pre-submission guideline audit; external TestFlight review.',
        identifiedDate: '2026-07-01',
      },
      {
        id: 'RSK-304',
        title: 'Backend API delivery behind app teams',
        description: 'API endpoints are trailing client needs, blocking integration.',
        category: 'Schedule',
        probability: 70,
        impact: 'critical',
        exposure: 180_000,
        status: 'open',
        owner: 'Leah Cohen',
        responseStrategy: 'mitigate',
        mitigation: 'Add a backend contractor; publish mock APIs to unblock client teams.',
        identifiedDate: '2026-06-18',
      },
    ],
    issues: [
      {
        id: 'ISS-301',
        title: 'Offline sync conflicts causing data loss in QA',
        description: 'Concurrent edits offline are lost on reconnect; conflict resolution is incomplete.',
        status: 'open',
        severity: 'critical',
        owner: 'Anna Schmidt',
        raisedDate: '2026-08-08',
        dueDate: '2026-09-10',
      },
      {
        id: 'ISS-302',
        title: 'iOS build pipeline flaky, blocking nightly builds',
        description: 'Intermittent signing failures are breaking nightly CI builds.',
        status: 'in-progress',
        severity: 'high',
        owner: 'Tom Wilson',
        raisedDate: '2026-07-15',
        dueDate: '2026-08-22',
      },
    ],
    changes: [
      {
        id: 'CR-301',
        title: 'Cut offline mode from v1 launch',
        type: 'scope',
        status: 'review',
        requestedBy: 'Jennifer Park',
        requestDate: '2026-08-06',
        impact: 'De-risks launch; defers offline to v1.1; -20 days.',
        costImpact: -50_000,
        scheduleImpact: -20,
        priority: 'high',
        justification: 'Offline sync is the top schedule and quality risk.',
        decision: '',
      },
      {
        id: 'CR-302',
        title: 'Add backend contractor for 3 months',
        type: 'resource',
        status: 'approved',
        requestedBy: 'Jennifer Park',
        requestDate: '2026-06-25',
        impact: 'Recovers the API timeline; unblocks client teams.',
        costImpact: 90_000,
        scheduleImpact: -10,
        priority: 'critical',
        justification: 'Backend API delivery is on the critical path.',
        decision: 'Approved by VP Product 2026-06-30.',
      },
      {
        id: 'CR-303',
        title: 'Reduce launch markets from 6 to 2',
        type: 'scope',
        status: 'rejected',
        requestedBy: 'Mark Rodriguez',
        requestDate: '2026-05-15',
        impact: 'Simpler launch; lowers reach.',
        costImpact: -30_000,
        priority: 'medium',
        justification: 'Considered as a way to de-risk the launch.',
        decision: 'Rejected — global launch is a committed OKR.',
      },
    ],
    decisions: [
      {
        id: 'DEC-301',
        title: 'Build native (Swift/Kotlin) rather than cross-platform',
        context: 'Weighed React Native/Flutter against native for performance and offline needs.',
        decision: 'Go native on both platforms to meet performance and offline requirements.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'Tom Wilson',
        decisionDate: '2026-03-25',
        flaggedForSteering: false,
      },
      {
        id: 'DEC-302',
        title: 'Adopt feature-flag gated releases',
        decision: 'Ship behind feature flags to allow partial rollout and fast rollback.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'Anna Schmidt',
        decisionDate: '2026-05-10',
        flaggedForSteering: false,
      },
      {
        id: 'DEC-303',
        title: 'Escalate schedule recovery plan to steering',
        context: 'SPI has fallen to 0.78; the committed launch date is at risk.',
        decision: 'Take a formal recovery plan (scope cut + added capacity) to the steering committee.',
        category: 'Governance',
        status: 'decided',
        decidedBy: 'Jennifer Park',
        decisionDate: '2026-08-10',
        flaggedForSteering: true,
      },
    ],
    actionItems: [
      {
        id: 'ACT-301',
        title: 'Deliver schedule recovery plan to sponsor',
        owner: 'Jennifer Park',
        dueDate: '2026-08-26',
        status: 'in-progress',
        flaggedForSteering: true,
      },
      {
        id: 'ACT-302',
        title: 'Fix offline sync conflict resolution',
        owner: 'Anna Schmidt',
        dueDate: '2026-09-05',
        status: 'open',
        flaggedForSteering: true,
      },
      {
        id: 'ACT-303',
        title: 'Complete App Store guideline pre-audit',
        owner: 'Tom Wilson',
        dueDate: '2026-09-20',
        status: 'open',
        flaggedForSteering: false,
      },
      {
        id: 'ACT-304',
        title: 'Onboard backend contractor',
        owner: 'Leah Cohen',
        dueDate: '2026-07-31',
        status: 'done',
        flaggedForSteering: false,
      },
    ],
    baselines: [],
    activeBaselineId: null,
    tags: ['mobile', 'ios', 'android'],
  },
  {
    id: 'PRJ-004',
    name: 'Customer Portal Redesign',
    description: 'Complete UX/UI overhaul of customer self-service portal with modern design system and improved workflows.',
    status: 'on-track',
    health: 'healthy',
    priority: 'medium',
    methodology: 'Agile',
    portfolioId: 'PORT-002',
    programId: 'PROG-002',
    startDate: '2026-03-16',
    endDate: '2026-09-29',
    completion: 82,
    budget: 420_000,
    spent: 348_000,
    cpi: 1.05,
    spi: 1.08,
    pm: 'Rachel Kim',
    sponsor: 'Diana Foster, VP CX',
    teamSize: 6,
    team: [
      { name: 'Rachel Kim', role: 'Product Manager', allocation: 100 },
      { name: 'Jordan Lee', role: 'Lead Designer', allocation: 100 },
      { name: 'Emma Davis', role: 'Frontend Lead', allocation: 100 },
      { name: 'Olivia Bennett', role: 'UX Researcher', allocation: 75 },
    ],
    scope: 'Redesign portal IA, build new design system, ship in 6 sprints.',
    objectives: ['Reduce support tickets by 30%', 'Improve NPS by 15 points', 'Launch new design system'],
    wbsTasks: portalTasks,
    risks: [
      {
        id: 'RSK-401',
        title: 'Content migration data quality gaps',
        description: 'Legacy portal content has inconsistent metadata that may not map cleanly.',
        category: 'Technical',
        probability: 40,
        impact: 'medium',
        exposure: 25_000,
        status: 'monitoring',
        owner: 'Olivia Bennett',
        responseStrategy: 'mitigate',
        mitigation: 'Automated validation; content freeze; SME review of top pages.',
        identifiedDate: '2026-07-10',
      },
      {
        id: 'RSK-402',
        title: 'Accessibility (WCAG 2.1 AA) findings surface late',
        description: 'Late audit findings could force rework close to launch.',
        category: 'Technical',
        probability: 35,
        impact: 'medium',
        exposure: 30_000,
        status: 'open',
        owner: 'Emma Davis',
        responseStrategy: 'mitigate',
        mitigation: 'Continuous axe scans in CI; external audit during UAT.',
        identifiedDate: '2026-08-01',
      },
      {
        id: 'RSK-403',
        title: 'User resistance to new navigation',
        description: 'Long-time users may struggle with the redesigned information architecture.',
        category: 'Organizational',
        probability: 30,
        impact: 'low',
        exposure: 15_000,
        status: 'mitigated',
        owner: 'Rachel Kim',
        responseStrategy: 'accept',
        mitigation: 'In-app guided tour; phased rollout with a feedback loop.',
        identifiedDate: '2026-06-15',
      },
    ],
    issues: [
      {
        id: 'ISS-401',
        title: 'Legacy SSO integration breaks on new portal shell',
        description: 'The new SPA shell fails the legacy SAML handshake for some tenants.',
        status: 'in-progress',
        severity: 'high',
        owner: 'Emma Davis',
        raisedDate: '2026-08-10',
        dueDate: '2026-09-02',
      },
    ],
    changes: [
      {
        id: 'CR-401',
        title: 'Add dark mode to design system',
        type: 'scope',
        status: 'approved',
        requestedBy: 'Diana Foster',
        requestDate: '2026-05-20',
        impact: 'Broadens appeal; +8 days.',
        costImpact: 18_000,
        scheduleImpact: 8,
        priority: 'low',
        justification: 'High customer demand in feedback surveys.',
        decision: 'Approved 2026-05-25.',
      },
      {
        id: 'CR-402',
        title: 'Phase rollout by customer segment',
        type: 'schedule',
        status: 'implemented',
        requestedBy: 'Rachel Kim',
        requestDate: '2026-07-01',
        impact: 'Lowers launch risk via staged exposure.',
        costImpact: 0,
        scheduleImpact: 5,
        priority: 'medium',
        justification: 'Safer than a big-bang launch for a high-traffic portal.',
        decision: 'Approved and in effect.',
      },
    ],
    decisions: [
      {
        id: 'DEC-401',
        title: 'Adopt in-house design system over third-party kit',
        context: 'Evaluated buying a component library vs building one aligned to brand.',
        decision: 'Build an in-house design system for long-term brand control and reuse.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'Jordan Lee',
        decisionDate: '2026-05-05',
        flaggedForSteering: false,
      },
      {
        id: 'DEC-402',
        title: 'Launch via phased segment rollout',
        decision: 'Roll out to low-risk segments first, then general availability.',
        category: 'Governance',
        status: 'decided',
        decidedBy: 'Rachel Kim',
        decisionDate: '2026-07-01',
        flaggedForSteering: true,
      },
    ],
    actionItems: [
      {
        id: 'ACT-401',
        title: 'Resolve SSO integration defect',
        owner: 'Emma Davis',
        dueDate: '2026-09-02',
        status: 'in-progress',
        flaggedForSteering: true,
      },
      {
        id: 'ACT-402',
        title: 'Complete external accessibility audit',
        owner: 'Emma Davis',
        dueDate: '2026-09-12',
        status: 'open',
        flaggedForSteering: false,
      },
      {
        id: 'ACT-403',
        title: 'Prepare launch communications and training',
        owner: 'Rachel Kim',
        dueDate: '2026-09-20',
        status: 'open',
        flaggedForSteering: false,
      },
    ],
    baselines: [],
    activeBaselineId: null,
    tags: ['portal', 'design', 'ux'],
  },
  {
    id: 'PRJ-005',
    name: 'Data Analytics Platform',
    description: 'Build modern data analytics platform on Snowflake with self-service BI capabilities for business users.',
    status: 'on-track',
    health: 'healthy',
    priority: 'high',
    methodology: 'Hybrid',
    portfolioId: 'PORT-001',
    programId: 'PROG-001',
    startDate: '2026-06-15',
    endDate: '2027-05-30',
    completion: 20,
    budget: 1_200_000,
    spent: 235_000,
    cpi: 1.01,
    spi: 0.99,
    pm: 'Marcus Chen',
    sponsor: 'Patricia Williams, CDO',
    teamSize: 7,
    team: [
      { name: 'Marcus Chen', role: 'Project Manager', allocation: 100 },
      { name: 'Priya Singh', role: 'Data Engineer Lead', allocation: 100 },
      { name: 'Sofia Reyes', role: 'Analytics Lead', allocation: 100 },
      { name: 'Oliver Brown', role: 'BI Developer', allocation: 100 },
      { name: 'Robert Taylor', role: 'Data Engineer', allocation: 75 },
    ],
    scope: 'Snowflake DW, dbt transforms, Looker semantic layer, self-service training.',
    objectives: ['Single source of truth for KPIs', 'Self-service BI for 100+ users', 'Reduce time-to-insight by 60%'],
    wbsTasks: analyticsTasks,
    risks: [
      {
        id: 'RSK-501',
        title: 'Source system data quality unknown',
        description: 'Upstream systems have undocumented quality issues that could undermine trust in the data.',
        category: 'Technical',
        probability: 60,
        impact: 'high',
        exposure: 150_000,
        status: 'open',
        owner: 'Priya Singh',
        responseStrategy: 'mitigate',
        mitigation: 'Early profiling; data contracts with source owners; a quarantine layer.',
        identifiedDate: '2026-06-25',
      },
      {
        id: 'RSK-502',
        title: 'Snowflake consumption cost overrun',
        description: 'Uncontrolled compute usage could exceed the consumption budget.',
        category: 'Financial',
        probability: 45,
        impact: 'medium',
        exposure: 80_000,
        status: 'monitoring',
        owner: 'Marcus Chen',
        responseStrategy: 'mitigate',
        mitigation: 'Resource monitors; auto-suspend; query optimization standards.',
        identifiedDate: '2026-07-05',
      },
      {
        id: 'RSK-503',
        title: 'Low self-service BI adoption',
        description: 'Business users may keep relying on spreadsheets instead of the new platform.',
        category: 'Organizational',
        probability: 50,
        impact: 'medium',
        exposure: 60_000,
        status: 'open',
        owner: 'Sofia Reyes',
        responseStrategy: 'mitigate',
        mitigation: 'Embedded analysts; office hours; curated starter dashboards.',
        identifiedDate: '2026-07-20',
      },
    ],
    issues: [
      {
        id: 'ISS-501',
        title: 'CRM connector API rate limits throttling ingestion',
        description: 'Fivetran syncs from the CRM are throttled, delaying initial loads.',
        status: 'in-progress',
        severity: 'medium',
        owner: 'Priya Singh',
        raisedDate: '2026-08-10',
        dueDate: '2026-09-08',
      },
    ],
    changes: [
      {
        id: 'CR-501',
        title: 'Add real-time streaming for ops dashboards',
        type: 'scope',
        status: 'review',
        requestedBy: 'Patricia Williams',
        requestDate: '2026-08-05',
        impact: 'Adds a streaming pipeline; +$110K, +25 days.',
        costImpact: 110_000,
        scheduleImpact: 25,
        priority: 'medium',
        justification: 'Operations wants near-real-time KPIs, not just daily batch.',
        decision: '',
      },
      {
        id: 'CR-502',
        title: 'Standardize semantic layer on Looker (drop Tableau POC)',
        type: 'scope',
        status: 'approved',
        requestedBy: 'Marcus Chen',
        requestDate: '2026-07-15',
        impact: 'Single BI tool; lowers license cost.',
        costImpact: -40_000,
        priority: 'medium',
        justification: 'Tool consolidation reduces cost and training overhead.',
        decision: 'Approved 2026-07-20.',
      },
    ],
    decisions: [
      {
        id: 'DEC-501',
        title: 'Adopt medallion (bronze/silver/gold) architecture',
        decision: 'Structure the warehouse into bronze/silver/gold layers for clear lineage and quality gates.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'Priya Singh',
        decisionDate: '2026-07-01',
        flaggedForSteering: false,
      },
      {
        id: 'DEC-502',
        title: 'Use dbt for all transformations',
        decision: 'Standardize on dbt for version-controlled, tested transformations.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'Robert Taylor',
        decisionDate: '2026-07-10',
        flaggedForSteering: false,
      },
      {
        id: 'DEC-503',
        title: 'Prioritize Finance and Sales domains for MVP',
        context: 'Limited capacity means the first release must target the highest-value domains.',
        decision: 'Deliver Finance and Sales analytics first; defer Supply Chain to a later release.',
        category: 'Scope',
        status: 'decided',
        decidedBy: 'Marcus Chen',
        decisionDate: '2026-07-25',
        flaggedForSteering: true,
      },
    ],
    actionItems: [
      {
        id: 'ACT-501',
        title: 'Complete source data profiling for Finance',
        owner: 'Priya Singh',
        dueDate: '2026-09-05',
        status: 'open',
        flaggedForSteering: true,
      },
      {
        id: 'ACT-502',
        title: 'Set up Snowflake resource monitors and budgets',
        owner: 'Marcus Chen',
        dueDate: '2026-08-30',
        status: 'in-progress',
        flaggedForSteering: false,
      },
      {
        id: 'ACT-503',
        title: 'Draft data governance and access policy',
        owner: 'Priya Singh',
        dueDate: '2026-09-18',
        status: 'open',
        flaggedForSteering: false,
      },
    ],
    baselines: [],
    activeBaselineId: null,
    tags: ['analytics', 'snowflake', 'bi'],
  },
  {
    id: 'PRJ-006',
    name: 'Cybersecurity Hardening',
    description: 'Enterprise security uplift including zero-trust architecture, MFA rollout, and SOC 2 compliance.',
    status: 'on-track',
    health: 'healthy',
    priority: 'critical',
    methodology: 'Waterfall',
    portfolioId: 'PORT-003',
    programId: 'PROG-003',
    startDate: '2026-04-01',
    endDate: '2027-01-29',
    completion: 50,
    budget: 950_000,
    spent: 470_000,
    cpi: 1.0,
    spi: 1.02,
    pm: 'Alex Brennan',
    sponsor: 'Karen Wright, CISO',
    teamSize: 5,
    team: [
      { name: 'Alex Brennan', role: 'Security PM', allocation: 100 },
      { name: 'Sam Patel', role: 'Security Architect', allocation: 100 },
      { name: 'Daniel Foster', role: 'SecOps Engineer', allocation: 100 },
      { name: 'Grace Liu', role: 'Compliance Analyst', allocation: 100 },
    ],
    scope: 'Zero-trust network, MFA enforcement, SIEM upgrade, SOC 2 audit prep.',
    objectives: ['Achieve SOC 2 Type II', 'Reduce security incidents by 60%', '100% MFA coverage'],
    wbsTasks: securityTasks,
    risks: [
      {
        id: 'RSK-601',
        title: 'SOC 2 evidence collection gaps',
        description: 'Manual evidence gathering may miss controls, jeopardizing the audit.',
        category: 'Compliance',
        probability: 50,
        impact: 'high',
        exposure: 120_000,
        status: 'open',
        owner: 'Grace Liu',
        responseStrategy: 'mitigate',
        mitigation: 'GRC tooling for automated evidence; monthly control checks.',
        identifiedDate: '2026-05-15',
      },
      {
        id: 'RSK-602',
        title: 'MFA rollout resistance in field offices',
        description: 'Field staff without company phones may resist MFA enrollment.',
        category: 'Organizational',
        probability: 55,
        impact: 'medium',
        exposure: 50_000,
        status: 'mitigated',
        owner: 'Alex Brennan',
        responseStrategy: 'mitigate',
        mitigation: 'Phased enrollment; help-desk surge; executive mandate.',
        identifiedDate: '2026-05-20',
      },
      {
        id: 'RSK-603',
        title: 'Legacy systems incompatible with zero-trust',
        description: 'Some legacy apps cannot support modern authentication or segmentation.',
        category: 'Technical',
        probability: 45,
        impact: 'high',
        exposure: 130_000,
        status: 'open',
        owner: 'Sam Patel',
        responseStrategy: 'mitigate',
        mitigation: 'Segment legacy behind a proxy; compensating controls; upgrade roadmap.',
        identifiedDate: '2026-07-01',
      },
      {
        id: 'RSK-604',
        title: 'SIEM migration data loss window',
        description: 'Cutover to the new SIEM risks a gap in log coverage.',
        category: 'Technical',
        probability: 30,
        impact: 'medium',
        exposure: 45_000,
        status: 'monitoring',
        owner: 'Daniel Foster',
        responseStrategy: 'mitigate',
        mitigation: 'Parallel logging during cutover; validation queries before decommission.',
        identifiedDate: '2026-07-20',
      },
    ],
    issues: [
      {
        id: 'ISS-601',
        title: 'Privileged accounts still without MFA',
        description: 'A subset of admin accounts remain exempt from MFA enforcement.',
        status: 'open',
        severity: 'high',
        owner: 'Daniel Foster',
        raisedDate: '2026-07-25',
        dueDate: '2026-09-01',
      },
      {
        id: 'ISS-602',
        title: 'SIEM ingesting duplicate events inflating alerts',
        description: 'Duplicate log sources are creating noisy, duplicate alerts.',
        status: 'in-progress',
        severity: 'medium',
        owner: 'Daniel Foster',
        raisedDate: '2026-08-05',
        dueDate: '2026-08-28',
      },
    ],
    changes: [
      {
        id: 'CR-601',
        title: 'Expand pen-test scope to include mobile app',
        type: 'scope',
        status: 'approved',
        requestedBy: 'Karen Wright',
        requestDate: '2026-06-20',
        impact: 'Broader coverage; +$40K, +10 days.',
        costImpact: 40_000,
        scheduleImpact: 10,
        priority: 'high',
        justification: 'The mobile app is in scope for SOC 2 and must be tested.',
        decision: 'Approved by CISO 2026-06-28.',
      },
      {
        id: 'CR-602',
        title: 'Adopt managed SOC (MDR) instead of in-house 24x7',
        type: 'resource',
        status: 'review',
        requestedBy: 'Alex Brennan',
        requestDate: '2026-08-01',
        impact: 'Faster 24x7 coverage; higher run-rate.',
        costImpact: 85_000,
        priority: 'medium',
        justification: 'Staffing a 24x7 in-house SOC is not feasible within the timeline.',
        decision: '',
      },
    ],
    decisions: [
      {
        id: 'DEC-601',
        title: 'Adopt Okta as primary identity provider',
        decision: 'Standardize on Okta for SSO and MFA across the enterprise.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'Sam Patel',
        decisionDate: '2026-04-25',
        flaggedForSteering: false,
      },
      {
        id: 'DEC-602',
        title: 'Pursue SOC 2 Type II (not Type I first)',
        context: 'Customers require an operating-effectiveness report, not just design.',
        decision: 'Go directly for SOC 2 Type II with a 6-month observation window.',
        category: 'Governance',
        status: 'decided',
        decidedBy: 'Alex Brennan',
        decisionDate: '2026-05-05',
        flaggedForSteering: true,
      },
      {
        id: 'DEC-603',
        title: 'Segment legacy systems behind a ZTNA proxy',
        decision: 'Place non-compliant legacy systems behind a zero-trust access proxy with compensating controls.',
        category: 'Technical',
        status: 'decided',
        decidedBy: 'Sam Patel',
        decisionDate: '2026-07-05',
        flaggedForSteering: false,
      },
    ],
    actionItems: [
      {
        id: 'ACT-601',
        title: 'Enforce MFA on all privileged accounts',
        owner: 'Daniel Foster',
        dueDate: '2026-09-01',
        status: 'in-progress',
        flaggedForSteering: true,
      },
      {
        id: 'ACT-602',
        title: 'Complete SOC 2 control gap remediation plan',
        owner: 'Grace Liu',
        dueDate: '2026-09-15',
        status: 'open',
        flaggedForSteering: true,
      },
      {
        id: 'ACT-603',
        title: 'Finalize MDR vs in-house SOC recommendation',
        owner: 'Alex Brennan',
        dueDate: '2026-08-30',
        status: 'open',
        flaggedForSteering: false,
      },
    ],
    baselines: [],
    activeBaselineId: null,
    tags: ['security', 'compliance', 'soc2'],
  },
];
